import { reaction } from 'mobx';
import { ErrorReporter } from './infrastructure/adapter/ErrorReporter';
import {
  AuthenticationUsecase,
  GoalUsecase,
  HistoryHandlerUsecase,
  KaizenLabServiceUsecase,
  OrganizationUsecase,
  PortfolioReportUsecase,
} from './application/usecase';
import { GoalStore, OrganizationStore, PortfolioReportStore, UiState } from './domain/store';

interface IDependencies {
  errorReporter: ErrorReporter;
  authenticationUsecase: AuthenticationUsecase;
  goalUsecase: GoalUsecase;
  historyHandlerUsecase: HistoryHandlerUsecase;
  kaizenLabServiceUsecase: KaizenLabServiceUsecase;
  organizationUsecase: OrganizationUsecase;
  portfolioReportUsecase: PortfolioReportUsecase;
  goalStore: GoalStore;
  organizationStore: OrganizationStore;
  portfolioReportStore: PortfolioReportStore;
  uiState: UiState;
}

export class Application {
  // private errorReporter: ErrorReporter;
  private authenticationUsecase: AuthenticationUsecase;
  private goalUsecase: GoalUsecase;
  private historyHandlerUsecase: HistoryHandlerUsecase;
  private kaizenLabServiceUsecase: KaizenLabServiceUsecase;
  private organizationUsecase: OrganizationUsecase;
  private portfolioReportUsecase: PortfolioReportUsecase;
  private goalStore: GoalStore;
  private organizationStore: OrganizationStore;
  private portfolioReportStore: PortfolioReportStore;
  private uiState: UiState;

  constructor({
    // errorReporter,
    authenticationUsecase,
    goalUsecase,
    historyHandlerUsecase,
    kaizenLabServiceUsecase,
    organizationUsecase,
    portfolioReportUsecase,
    goalStore,
    organizationStore,
    portfolioReportStore,
    uiState,
  }: IDependencies) {
    // this.errorReporter = errorReporter;
    this.authenticationUsecase = authenticationUsecase;
    this.goalUsecase = goalUsecase;
    this.historyHandlerUsecase = historyHandlerUsecase;
    this.kaizenLabServiceUsecase = kaizenLabServiceUsecase;
    this.organizationUsecase = organizationUsecase;
    this.portfolioReportUsecase = portfolioReportUsecase;
    this.goalStore = goalStore;
    this.organizationStore = organizationStore;
    this.portfolioReportStore = portfolioReportStore;
    this.uiState = uiState;
  }

  public async boot() {
    const {
      authenticationUsecase,
      goalUsecase,
      historyHandlerUsecase,
      kaizenLabServiceUsecase,
      organizationUsecase,
      portfolioReportUsecase,
      goalStore,
      organizationStore,
      portfolioReportStore,
      uiState,
    } = this;
    try {
      await authenticationUsecase.authenticate();
      await organizationUsecase.getOrganizations();
      await goalUsecase.getGoals();
      await goalUsecase.setInitialSelectedGoalId();
      await portfolioReportUsecase.setInitialTargetDeviceFilters();
      await portfolioReportUsecase.setInitialStateFilters();
      await portfolioReportUsecase.setInitialRoundPurposeFilters();
    } catch (e) {
      console.error(e);
    }
    await kaizenLabServiceUsecase.getKaizenLabServiceList();
    if (goalStore.goalList.length > 0) {
      historyHandlerUsecase.manipulateHistory(
        'replace',
        organizationStore.selectedOrganizationId,
        goalStore.selectedGoalId,
        portfolioReportStore.targetDeviceFilters,
        portfolioReportStore.stateFilters,
        portfolioReportStore.roundPurposeFilters,
      );
      portfolioReportUsecase.getPortfolioReport(goalStore.selectedGoalId);
    }
    uiState.setReady(true);
    this.reactions();
  }

  private reactions() {
    const {
      goalUsecase,
      historyHandlerUsecase,
      kaizenLabServiceUsecase,
      portfolioReportUsecase,
      goalStore,
      organizationStore,
      portfolioReportStore,
      uiState,
    } = this;

    reaction(
      () => organizationStore.selectedOrganization,
      async () => {
        await kaizenLabServiceUsecase.getKaizenLabServiceList();
        try {
          await goalUsecase.getGoals();
        } catch (e) {
          console.error(e);
        }
        portfolioReportUsecase.clearFilters();
        historyHandlerUsecase.manipulateHistory(
          'replace',
          organizationStore.selectedOrganizationId,
          goalStore.selectedGoalId,
          portfolioReportStore.targetDeviceFilters,
          portfolioReportStore.stateFilters,
          portfolioReportStore.roundPurposeFilters,
        );
      },
    );

    reaction(
      () => goalStore.selectedGoalId,
      async (selectedGoalId: string) => {
        historyHandlerUsecase.manipulateHistory(
          'replace',
          organizationStore.selectedOrganizationId,
          goalStore.selectedGoalId,
          portfolioReportStore.targetDeviceFilters,
          portfolioReportStore.stateFilters,
          portfolioReportStore.roundPurposeFilters,
        );
        if (selectedGoalId) {
          portfolioReportUsecase.getPortfolioReport(selectedGoalId);
        }
      },
    );

    reaction(
      // https://mobx.js.org/best/react.html#correct-access-array-functions-in-tracked-function
      () =>
        portfolioReportStore.targetDeviceFilters.join(', ') +
        ', ' +
        portfolioReportStore.stateFilters.join(', ') +
        ', ' +
        portfolioReportStore.roundPurposeFilters.join(', '),
      async () => {
        historyHandlerUsecase.manipulateHistory(
          'replace',
          organizationStore.selectedOrganizationId,
          goalStore.selectedGoalId,
          portfolioReportStore.targetDeviceFilters,
          portfolioReportStore.stateFilters,
          portfolioReportStore.roundPurposeFilters,
        );
      },
    );

    reaction(
      () => goalStore.isLoading,
      (isLoading: boolean) => {
        if (!isLoading) {
          uiState.setReady(true);
        } else {
          uiState.setReady(false);
        }
      },
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Application.ts